import { Button, Checkbox, H3, Textarea, TextEditor } from '@base'
import { Spacings } from '@styles'
import { useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { Label } from '../../styles'
import { Add, ExposureZero, Remove } from '@material-ui/icons'
import { strings } from '@constants'
import BasicModal from '@base/basic/BasicModal'

const FormElementWrapper = styled.div`
	display: flex;
	flex-direction: row;
	& > div {
		&:nth-child(1) {
			width: 78%;
		}
	}
	gap: ${Spacings.SPACING_2B};
	flex: 1;
`

const OptionsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-around;
	flex: 1;
	padding: ${Spacings.SPACING_2B} 0;
`

const ChechboxLabel = styled(Label)`
	display: flex;
	align-items: center;
	cursor: pointer;
`

const CheckBoxWrapper = styled.div`
	display: flex;
	flex-direction: row;
	// justify-content: ;
	gap: ${Spacings.SPACING_2B};
	align-items: center;
`

const MainWrapper = styled.div`
	padding: ${Spacings.SPACING_2B} 0;
`

const ButtonsWrapper = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: end;
	gap: ${Spacings.SPACING_2B};
`
const Heading = styled(H3)``

const ToggleOptionsWrapper = styled.div`
	border: ${Spacings.SPACING_2} solid
		${({ theme, selectedOption }) => {
			if (selectedOption === 0) {
				return `${theme.palette.background.pastelRed}`
			} else if (selectedOption === 1) {
				return `${theme.palette.background.pastelBlue}`
			} else if (selectedOption === 2) {
				return `${theme.palette.background.pastelGreen}`
			}
		}};
	border-radius: ${Spacings.SPACING_3B};
	box-shadow: 0 0 ${Spacings.SPACING_4B} ${Spacings.SPACING_1B} rgb(0 0 0 / 5%);
	&:hover {
		box-shadow: 0 0 ${Spacings.SPACING_4B} ${Spacings.SPACING_2B} rgb(0 0 0 / 5%);
	}

	overflow: hidden;
	vertical-align: middle;
	display: flex;
`

const ToggleOption = styled.button`
	flex: 1;
	border: none;
	padding: ${Spacings.SPACING_1};
	background: none;
	text-emphasis: circle;
	&:hover {
		cursor: pointer;
		background-color: ${({ theme, selected }) => !selected && theme.palette.background.blueLightest};
	}
	${({ selected, option }) => {
		if (selected) {
			if (option === 0) {
				return css`
					background-color: ${({ theme }) => theme.palette.background.pastelRed};
					color: ${({ theme }) => theme.palette.text.white};
				`
			} else if (option === 1) {
				return css`
					background-color: ${({ theme }) => theme.palette.background.pastelBlue};
					color: ${({ theme }) => theme.palette.text.white};
				`
			} else if (option === 2)
				return css`
					background-color: ${({ theme }) => theme.palette.background.pastelGreen};
					color: ${({ theme }) => theme.palette.text.white};
				`
		}
	}}
`

const TextEditorWaaper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
`

const ActionButton = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: ${Spacings.SPACING_4B};
`

const ButtonOptions = [
	['undo', 'redo'],
	['font', 'fontSize', 'formatBlock'],
	['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
	['fontColor', 'hiliteColor'],
	['align', 'list', 'lineHeight'],
	['outdent', 'indent'],
	['table', 'horizontalRule', 'link'],
	['fullScreen', 'showBlocks', 'codeView'],
]

const EditOptions = ({
	selectedToggleState,
	toggleOptionHandler,
	checkValue,
	onCheckHandler,
	options,
	onButtonClick,
	disabled,
	textValue,
	onTextChange,
	label,
	...props
}) => {
	// const [selectedOption, setSelectedOption] = useState(0)
	const [open, setOpen] = useState(false)

	const onCheck = (val) => {
		val = val.target.checked
		onCheckHandler(val)
	}

	const onViewHandler = () => {
		setOpen(true)
	}

	const onSaveHandler = () => {
		onButtonClick()
		setOpen(false)
	}

	return (
		<OptionsWrapper>
			<ToggleOptionsWrapper selectedOption={selectedToggleState}>
				{options?.map((option) => {
					return (
						<ToggleOption
							key={option.value}
							value={option.value}
							onClick={() => toggleOptionHandler(option.value)}
							selected={selectedToggleState === option.value}
							option={option.value}
							disabled={disabled}
						>
							{option?.label}
						</ToggleOption>
					)
				})}
			</ToggleOptionsWrapper>
			<CheckBoxWrapper>
				<Checkbox size={1.2} disabled={disabled} checked={checkValue} value={checkValue} onChange={onCheck} />
				<ChechboxLabel>{'Show in CRAN'}</ChechboxLabel>
			</CheckBoxWrapper>
			<ButtonsWrapper>
				<Button xs type='primary' onClick={onViewHandler} disabled={disabled}>
					{textValue ? strings('edit') : strings('add')}
				</Button>
			</ButtonsWrapper>
			<BasicModal isOpen={open} onClose={() => setOpen(false)} closeIcon={false}>
				<TextEditorWaaper>
					<Heading>{label}</Heading>
					<TextEditor height={'40vh'} closeIcon={false} value={textValue} onChange={onTextChange} ButtonOptions={ButtonOptions} />
					<ActionButton>
						<Button xs type='secondary' variant='destructive' onClick={() => setOpen(false)}>
							{strings('cancel')}
						</Button>
						<Button xs type='primary' onClick={onSaveHandler}>
							{strings('save')}
						</Button>
					</ActionButton>
				</TextEditorWaaper>
			</BasicModal>
		</OptionsWrapper>
	)
}

const ProbeTextArea = ({
	toggleState = null,
	onToggleChange,
	label,
	placeholder,
	textValue,
	onTextChange,
	checkValue,
	onCheckHandler,
	onButtonClick,
	disabled,
	...props
}) => {
	const [selectedToggleState, setSelectedToggleState] = useState(1)

	const options = useMemo(
		() => [
			{ label: <Remove fontSize='small' />, selected: selectedToggleState === 0, value: 0 },
			{ label: <ExposureZero fontSize='small' />, selected: selectedToggleState === 1, value: 1 },
			{ label: <Add fontSize='small' />, selected: selectedToggleState === 2, value: 2 },
		],
		[selectedToggleState]
	)
	const toggleOptionHandler = (value) => {
		setSelectedToggleState(value)
		onToggleChange(value === 0 ? false : value === 2 ? true : null)
	}

	useMemo(() => {
		if (toggleState !== null) {
			setSelectedToggleState(toggleState === true ? 2 : 0)
		} else {
			setSelectedToggleState(1)
		}
	}, [toggleState])

	const getPlainText = (html) => {
		const div = document.createElement('div')
		div.innerHTML = html
		return div.textContent || div.innerText || ''
	}

	return (
		<MainWrapper>
			<FormElementWrapper>
				<Textarea
					bigText
					rows={4}
					disabled={true}
					label={label}
					placeholder={placeholder}
					value={getPlainText(textValue || '')}
					onChange={onTextChange}
				/>
				<EditOptions
					props={props}
					selectedToggleState={selectedToggleState}
					// setSelectedToggleState={setSelectedToggleState}
					options={options}
					onCheckHandler={onCheckHandler}
					checkValue={checkValue}
					toggleOptionHandler={toggleOptionHandler}
					disabled={disabled}
					onButtonClick={onButtonClick}
					textValue={textValue}
					onTextChange={onTextChange}
					label={label}
				/>
			</FormElementWrapper>
		</MainWrapper>
	)
}

export default ProbeTextArea
